import type { TitlePlate } from '@nine/shared';
import type { Hex } from '../icons/pix';
import type { Pen } from './pen';

/**
 * The title plate registry: each plate (`TitlePlate`, docs/art/titles-ref/) is one file under
 * `plates/themes/`, which calls `registerPlate` when it loads. A plate is data plus drawing: the
 * colours of its frame and lettering, how far its ornaments reach, and the ornaments themselves,
 * drawn in plate units (plates/pen.ts) at any scale.
 */

/** The frame of a plate, in units on its canvas, as ornaments see it. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Right and bottom edges (x + w, y + h). */
  readonly r: number;
  readonly b: number;
  readonly cx: number;
  readonly cy: number;
}

/** Three tones of one colour: light (upper right), base, shade. */
export type Tones = readonly [Hex, Hex, Hex];

/** A tassel hanging under the frame. */
export interface Tassel {
  /** Units from the frame's left edge; negative counts from its right edge. */
  readonly x: number;
  /** Its threads (red, after the master's ribbons and tassels, when left out). */
  readonly tones?: Tones;
  /** What hangs on the cord above the threads. */
  readonly charm?: 'knot' | 'coin' | 'jade' | 'bead' | 'bell';
  /** The charm's colours, when not gold. */
  readonly charmTones?: Tones;
  /** Units of cord above the charm. */
  readonly cord?: number;
  /** Units of thread. */
  readonly len?: number;
}

/** Something that moves on a plate, drawn once per scale and placed each frame. */
export interface PlateSprite {
  readonly w: number;
  readonly h: number;
  draw(p: Pen): void;
}

/** Places a plate's sprites (units on its canvas) for one moment. */
export interface PlateAnim {
  spr(name: string, x: number, y: number, alpha?: number): void;
}

export interface PlateDef {
  readonly id: TitlePlate;
  /** The plate's name on the reference sheet (Thai), for the review page. */
  readonly name: string;
  /** The field inside the frame: light, base, shade. */
  readonly ground: Tones;
  /** The thin inner border line (gold on lacquer, maroon on paper). */
  readonly trim: Hex;
  /** The rails' metal; the master's gold when left out. */
  readonly rail?: Tones;
  /** The dark, slightly coloured outline round the frame and ornaments; ink when left out. */
  readonly ink?: Hex;
  /** The lettering: its fill from top to bottom, and its outline. */
  readonly text: { readonly fill: readonly [Hex, Hex]; readonly line: Hex };
  /** Units kept clear for ornaments inside the frame, left and right of the lettering. */
  readonly inset: readonly [number, number];
  /** Ornaments behind the frame (a moon, a sun, cloth). */
  back?(p: Pen, f: Box): void;
  /** Ornaments over the frame. */
  front?(p: Pen, f: Box): void;
  /** Tassels; two red ones at the ends when left out. */
  readonly tassels?: readonly Tassel[];
  /** Gold knobs on the frame's ends (on unless false). */
  readonly knobs?: boolean;
  /** Its light motion: sprites, and where they are at time `t` (seconds). */
  readonly motion?: {
    readonly sprites: Readonly<Record<string, PlateSprite>>;
    play(a: PlateAnim, t: number, f: Box): void;
  };
}

const defs = new Map<TitlePlate, PlateDef>();

/** Declares a plate (once, from its file under plates/themes/). */
export function registerPlate(def: PlateDef): PlateDef {
  if (defs.has(def.id)) throw new Error(`Plate "${def.id}" is registered twice`);
  defs.set(def.id, def);
  return def;
}

export const plateDef = (id: TitlePlate): PlateDef | undefined => defs.get(id);
export const plateDefs = (): readonly PlateDef[] => [...defs.values()];
